import React, {memo, useCallback} from 'react';
import {View, Pressable, StyleSheet, Text} from 'react-native';
import Styles from '../../Styles';
import AccountStore from 'src/stores/AccountStore';
import AVText from '../../elements/AVText';
import AVFormattedCurrency from '../../elements/AVFormattedCurrency';
import TrashIcon from '../../img/svg/TrashIcon';
import {CartItem} from '../../../types/TransactionDetail';
import Localized from 'src/constants/AppStrings';
import SupLeafIcon from 'src/components/img/svg/SupLeafIcon';
import {scanAndPayTemplate} from './templates/scanandPayThemeHelper';
import {getDescriber} from 'src/components/elements/cart/descriptor/DescriptorType';

import {toNum} from './utils/promo';
import {useSortedModifiers} from './hooks/useSortedModifiers';
import {useDiscounts} from './hooks/useDiscounts';

import DiscountLine from './DiscountLine';
import ModifierLine from './ModifierLine';
import RewardDiscount from 'src/components/screens/scanAndPay/RewardDiscount';

const styles = getDescriber().cartProductStyleDescriptor();

const safe = (node: React.ReactNode) =>
  typeof node === 'string' || typeof node === 'number' ? (
    <AVText>{String(node)}</AVText>
  ) : (
    node
  );

const {caloriePromotionItem} = getDescriber();
const {
  discountTagColor,
  checkoutStyles,
  deleteIconDimension,
  deleteIconColor,
} = scanAndPayTemplate;

export interface CartProductItemProps {
  trashClick?: (lineNumbers: number[]) => void;
  item: CartItem;
  editClicked?: (item: CartItem) => void;
  canEdit: boolean;
  canDelete: boolean;
}

const CartProductItem = memo(function CartProductItem({
  trashClick,
  item,
  editClicked,
  canEdit,
  canDelete,
}: CartProductItemProps) {
  const onRowPress = useCallback(
    () => editClicked?.(item),
    [editClicked, item],
  );
  const onTrashPress = useCallback(
    () => trashClick?.([item.LineNumber]),
    [trashClick, item.LineNumber],
  );

  const modifiers = useSortedModifiers(item?.Modifiers ?? []);
  const {onscreen: discounts} = useDiscounts(item?.Discounts ?? []);
  const inlineDiscountPresent =
    !!item.Discount && (toNum(item.Discount) ?? 0) > 0;
  const hasModifiers = modifiers.length > 0;
  const makeKey = (d: (typeof discounts)[number], idx: number) =>
    (d.discountHeaderId?.toString() ?? `${d.displayName}-${idx}`) as string;

  const rewardsPoints = item?.Points > 0 && (
    <View style={styles.cartItemMainKey}>
      <Text style={styles.cartRewardPoints}>
        {item?.Points} {Localized.Labels.points.toLowerCase()}
      </Text>
    </View>
  );

  const supFeeRow =
    item.TransactionChargeName && item.TransactionChargeAmount ? (
      <View style={styles.cartItemMainKey}>
        <View style={checkoutStyles.cartDescriptionSubView}>
          <View style={[styles.cartTagIcon, styles.supIconMargin]}>
            <SupLeafIcon />
          </View>
          <View
            style={styles.cartPromoTextView}
            accessible
            testID="qa_Checkout.SUPFeeLabel_txt"
            nativeID="qa_Checkout.SUPFeeLabel_txt"
          >
            <AVText
              accessibilityLabel={Localized.Labels.sup_fee}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
              style={[styles.description, styles.supDescription]}
              numberOfLines={1}
            >
              {Localized.Labels.sup_fee}
            </AVText>
          </View>
        </View>
        <AVFormattedCurrency
          style={styles.cartPricePromo}
          value={item.TransactionChargeAmount}
          accessibilityLabel={`SUP amount $${item.TransactionChargeAmount}`}
          currency={AccountStore.getCurrency()}
          numberOfLines={1}
          accessible
          testID="qa_Checkout.SUPFeeValue_txt"
          nativeID="qa_Checkout.SUPFeeValue_txt"
        />
      </View>
    ) : null;

  const showCalculatedPrice =
    scanAndPayTemplate.toggleStylesAndCurrencies && item?.Points >= 0 && discounts.length > 0;

  const nutritionPromo = item.NutritionColorDiscount
    ? safe(
        caloriePromotionItem(
          item.NutritionColorDiscount,
          item.NutritionColorDisplayName,
          item.NutritionDisplayColor,
        ),
      )
    : null;

  const hasNegativePoints = (item?.Points ?? 0) < 0;
  const price = hasNegativePoints
    ? item?.OriginalPrice ?? item?.Price
    : item?.Price;
  return (
    <Pressable
      accessibilityLabel={'Edit product ' + item?.Description}
      accessibilityRole="button"
      accessibilityHint={`Double tap to edit ${item?.Description} from your cart`}
      role="button"
      nativeID="qa_Checkout.EditProductContainer"
      style={styles.container}
      onPress={onRowPress}
      disabled={!canEdit}
      accessible
      testID="qa_Checkout.EditProductContainer"
    >
      <View style={[styles.mainSection, styles.alignStart]}>
        {canDelete && (
          <Pressable
            accessibilityLabel="Delete product"
            nativeID="qa_Checkout.DeleteProduct_btn"
            accessibilityHint={`Double tap to delete ${item.Description} from your cart`}
            style={styles.deleteButton}
            onPress={onTrashPress}
            accessible
            testID="qa_Checkout.DeleteProduct_btn"
          >
            <TrashIcon size={deleteIconDimension} color={deleteIconColor} />
          </Pressable>
        )}

        <View style={styles.cartDescriptionView}>
          <View style={styles.productCartItem}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm3}
              style={[styles.cartProductDescription, styles.titleMargin]}
              accessible
              testID="qa_Checkout.ProductName_txt"
              nativeID="qa_Checkout.ProductName_txt"
            >
              {item.Description}
            </AVText>

            <AVFormattedCurrency
              style={[styles.cartPrice, item?.Points < 0 && styles.rewardPrice]}
              value={toNum(price) ?? 0}
              currency={AccountStore.getCurrency()}
              numberOfLines={1}
              accessible
              testID="qa_Checkout.ProductPrice_txt"
              nativeID="qa_Checkout.ProductPrice_txt"
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
            />
          </View>
          <RewardDiscount
            points={item.Points}
            isReward={item?.isFreeRewardProduct}
          />
          {rewardsPoints}
          <View style={[styles.cartItemMainKey]}>
            <View style={styles.cartDescriptionView}>
              {discounts.map((d, i) =>
                safe(
                  <DiscountLine
                    key={makeKey(d, i)}
                    discount={{
                      discountHeaderId: d.discountHeaderId,
                      discount: toNum(d.discount) ?? 0,
                      displayName: d.displayName,
                      isStackedDiscount: d.isStackedDiscount,
                    }}
                    color={discountTagColor}
                    testID="qa_Checkout.OnScreenDiscount_txt"
                  />,
                ),
              )}
            </View>
            {showCalculatedPrice && (
              <AVFormattedCurrency
                style={styles.cartPricePromo}
                value={parseFloat(item.Price) - parseFloat(item.Discount)}
                accessibilityLabel="afterDiscountItemPrice"
                currency={AccountStore.getCurrency()}
                numberOfLines={1}
              />
            )}
          </View>
          {supFeeRow}

          {hasModifiers &&
            safe(
              <View style={[styles.cartItemMainKey, styles.column]}>
                {modifiers.map((m, idx) =>
                  safe(
                    <ModifierLine
                      key={`${m.SeqNum ?? m.Name}-${m.Type ?? 't'}-${idx}`}
                      modifier={m}
                      isFirst={idx === 0}
                    />,
                  ),
                )}
              </View>,
            )}

          {nutritionPromo}

          {item.SoldOut && (
            <AVText accessibilityLabel="Sold out" style={styles.status}>
              {Localized.Labels.sold_out}
            </AVText>
          )}
        </View>
      </View>
    </Pressable>
  );
});

export default CartProductItem;
